import { useEffect } from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useTranslation } from 'react-i18next'

import { Box, Button, Flex, Text } from '@chakra-ui/react'

import { TrashIcon } from '@hivewatch/dark'

import { DeviceFeed } from '@/features/device-feed'
import {
  MSEVideoPlayer,
  VideoFeedEvent,
  VideoPlayer,
  useFindDeviceFeed,
} from '@/features/video-player'
import { useDevicePopoverDetailsQuery } from '@/graphql/generated/hooks'
import { DeviceType } from '@/graphql/generated/schemas'
import { AnalyticsScreen, useMixpanel } from '@/utils/analytics'

import { getDeviceIcon } from '../../utils/DeviceIcons'

interface DevicePopoverContentIProps {
  deviceId: string
  deviceName: string
  deviceType: DeviceType
  showRemoveAction?: boolean
  canRemove?: boolean
  isRemoving?: boolean
  onRemove?: () => void
}

export const DevicePopoverContent = ({
  deviceId,
  deviceName,
  deviceType,
  showRemoveAction = false,
  canRemove = false,
  isRemoving = false,
  onRemove,
}: DevicePopoverContentIProps) => {
  const { t } = useTranslation()
  const isCameraDevice = deviceType === DeviceType.Camera
  const icon = getDeviceIcon(deviceType)

  // Feature flag for MSE-based authenticated proxy streaming
  const isMSEStreamingEnabled = useFeatureFlag(
    FeatureFlag.authenticatedProxyStreaming
  )

  const { data, loading: isLoading } = useDevicePopoverDetailsQuery({
    variables: {
      id: deviceId,
    },
    nextFetchPolicy: 'cache-first',
  })

  const { createCameraEventHandler, markStreamRequested } = useMixpanel(
    AnalyticsScreen.DeviceHoverModal
  )

  useEffect(() => {
    markStreamRequested()
  }, [])

  const mixpanelData = () => {
    return {
      device_id: deviceId,
      facility_id: data?.device?.facility?.id,
      floor_id: data?.device?.floor?.id,
      floor_name: data?.device?.floor?.name,
      streaming_type: data?.device?.camera?.streamingType,
    }
  }

  const handleCameraEvent = createCameraEventHandler(mixpanelData())

  const {
    stream: { url: cameraFeedUrl, isLoading: isFeedDataLoading, errorMessage },
    nextCamera,
    previousCamera,
    availableCamerasIds,
    currentCameraId,
  } = useFindDeviceFeed(deviceId, handleCameraEvent)

  return (
    <Flex data-testid='DevicePopoverContent:wrapper' direction='column'>
      {isCameraDevice && (
        <Box p='8px' shadow='xl'>
          <DeviceFeed
            isLoading={isFeedDataLoading || isLoading}
            onClickNext={nextCamera}
            onClickPrevious={previousCamera}
            showCameraSwitcher={availableCamerasIds.length > 1}
          >
            {isMSEStreamingEnabled && currentCameraId ? (
              <MSEVideoPlayer
                autoplay={true}
                cameraId={currentCameraId}
                errorLabel={errorMessage}
                onEvent={handleCameraEvent}
                onStartPlaying={() => handleCameraEvent(VideoFeedEvent.Playing)}
                placeholderHeight='204px'
                showControls={true}
              />
            ) : (
              <VideoPlayer
                autoplay={true}
                errorLabel={errorMessage}
                liveEnabled={true}
                liveOnly
                onStartPlaying={() => handleCameraEvent(VideoFeedEvent.Playing)}
                pipActive={false}
                placeholderHeight='204px'
                url={cameraFeedUrl}
              />
            )}
          </DeviceFeed>
        </Box>
      )}
      <Flex alignItems='center' direction='row' p='16px' shadow='md'>
        {icon}
        <Text
          color='black'
          flex='1'
          fontSize='16px'
          fontWeight='medium'
          marginLeft='12px'
          minW='0'
          noOfLines={1}
        >
          {deviceName}
        </Text>
        {showRemoveAction && (
          <Button
            aria-label={t('floors:plan_remove_device_action')}
            isDisabled={!canRemove}
            isLoading={isRemoving}
            leftIcon={<TrashIcon color='currentColor' size={16} />}
            loadingText={t('floors:plan_remove_device_loading')}
            ml='12px'
            onClick={onRemove}
            size='sm'
            variant='danger'
          >
            {t('floors:plan_remove_device_action')}
          </Button>
        )}
      </Flex>
    </Flex>
  )
}
